// hooks/finance/useMaxDates.js
import { useQuery } from '@tanstack/react-query';
import { reactQueryFinanceDefaults } from './queryDefaults';
import apiService from '../../services/api';
import { ENDPOINTS } from '../../services/endpoints';

/**
 * Helper function to extract date-only string from datetime
 * @param {string} dateStr - Date or datetime string
 * @returns {string|null} - Date string in 'YYYY-MM-DD' format or null
 */
const formatDateOnly = (dateStr) => {
  if (!dateStr) return null;
  
  // If it's already in YYYY-MM-DD format (no 'T'), return as-is
  if (!dateStr.includes('T')) return dateStr;
  
  // Extract date part from ISO datetime (before 'T')
  return dateStr.split('T')[0];
};

export const useMaxDates = () => {
  const fetchMaxDates = async () => {
    try {
      const response = await apiService.get(ENDPOINTS.FINANCE_MAX_DATES);
      
      // Try different possible response structures
      let maxDateUpdated, maxTransactionDate;
      
      if (response.data) {
        maxDateUpdated = response.data.max_date_updated;
        maxTransactionDate = response.data.max_transaction_date;
      } else {
        maxDateUpdated = response.max_date_updated;
        maxTransactionDate = response.max_transaction_date;
      }
      
      // Standardize to date-only format (YYYY-MM-DD)
      return {
        maxDateUpdated: formatDateOnly(maxDateUpdated),
        maxTransactionDate: formatDateOnly(maxTransactionDate),
      };
    } catch (error) {
      console.error('Error fetching max dates:', error);
      throw error;
    }
  };

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['financeMaxDates'],
    queryFn: fetchMaxDates,
    ...reactQueryFinanceDefaults,
    staleTime: 5 * 60 * 1000, // 5 minutes - max dates don't change often
  });

  return {
    maxDates: data || {
      maxDateUpdated: null,
      maxTransactionDate: null,
    },
    loading: isLoading,
    refreshing: isFetching,
    error: isError ? (error?.message || 'Failed to load max dates') : null,
    isError,
    initialized: !!data,
    refetch,
  };
};